import { defineStore, storeToRefs } from 'pinia'
import { type Raw, computed, ref, shallowRef } from 'vue'

import { AppNativeSDK } from '@/modules/native-app'
import { getNativePurchaseBundleId } from '@/modules/native-payments/app-context'
import { isNativePurchaseCapabilities } from '@/modules/native-payments/helpers'
import type { NativePaymentMessage, NativePurchaseCapabilities } from '@/modules/native-payments/types'

import { useAppStore } from './app-store'

type NativeAppMessage = {
    type: string
    payload: Record<string, unknown>
}
type NativeAppMessageHandler = (data: unknown) => void
type NativeAppOutgoingMessageHandler = (message: NativeAppMessage) => void

const ADJUST_ACTIVE_TIME_EVENT_TOKEN = 'tcw2fl'
const ACTIVE_TIME_TARGET_MS = 10 * 60 * 1000 // 10 minutes
const PING_INTERVAL_MS = 15_000 // 15 sec
const CAPABILITIES_TIMEOUT_MS = 10_000

type ResolvedPurchaseCapabilities = NativePurchaseCapabilities & { bundleId: string }

export const useNativeAppStore = defineStore(
    'native_app',
    () => {
        const { platform, isNativeAppEnv } = storeToRefs(useAppStore())

        const sdk = shallowRef<Raw<AppNativeSDK>>()
        const purchaseCapabilities = shallowRef<ResolvedPurchaseCapabilities>()
        let capabilitiesRequest:
            | {
                  promise: Promise<ResolvedPurchaseCapabilities>
                  resolve: (value: ResolvedPurchaseCapabilities) => void
                  reject: (reason: unknown) => void
                  timeout: ReturnType<typeof setTimeout>
              }
            | undefined
        const elapsedActiveTimeMs = ref(0)
        const launchCount = ref(0)
        const isActiveTimeTracked = computed(() => elapsedActiveTimeMs.value >= ACTIVE_TIME_TARGET_MS)
        const messageHandlers = new Set<NativeAppMessageHandler>()
        const outgoingMessageHandlers = new Set<NativeAppOutgoingMessageHandler>()

        let pingIntervalId: ReturnType<typeof setInterval> | undefined

        const isAvailable = computed(() => sdk.value?.isAvailable ?? false)

        function init() {
            if (!isNativeAppEnv.value || sdk.value) {
                return
            }
            launchCount.value += 1
            sdk.value = new AppNativeSDK(platform.value)
            sdk.value.addMessageHandler(handlePurchaseCapabilities)
            sdk.value.init()
            messageHandlers.forEach((handler) => sdk.value?.addMessageHandler(handler))
            startActiveTracking()
        }

        function clearCapabilitiesRequest() {
            if (capabilitiesRequest) {
                clearTimeout(capabilitiesRequest.timeout)
                capabilitiesRequest = undefined
            }
        }

        function handlePurchaseCapabilities(data: unknown) {
            if (!data || typeof data !== 'object') {
                return
            }
            const { type, payload } = data as NativePaymentMessage
            if (
                type !== 'native:purchase.capabilities' ||
                !payload ||
                typeof payload !== 'object' ||
                !isNativePurchaseCapabilities(payload)
            ) {
                return
            }
            purchaseCapabilities.value = { ...payload, bundleId: getNativePurchaseBundleId(payload) }
            capabilitiesRequest?.resolve(purchaseCapabilities.value)
            clearCapabilitiesRequest()
        }

        function getPurchaseCapabilities(): Promise<ResolvedPurchaseCapabilities> {
            if (purchaseCapabilities.value) {
                return Promise.resolve(purchaseCapabilities.value)
            }
            if (capabilitiesRequest) {
                return capabilitiesRequest.promise
            }
            init()
            if (!isAvailable.value) {
                return Promise.reject(new Error('Native app is unavailable'))
            }

            let resolve!: (value: ResolvedPurchaseCapabilities) => void
            let reject!: (reason: unknown) => void
            const promise = new Promise<ResolvedPurchaseCapabilities>((onResolve, onReject) => {
                resolve = onResolve
                reject = onReject
            })
            capabilitiesRequest = {
                promise,
                resolve,
                reject,
                timeout: setTimeout(() => {
                    reject(new Error('Native app did not return payment capabilities'))
                    clearCapabilitiesRequest()
                }, CAPABILITIES_TIMEOUT_MS),
            }
            try {
                postMessage('purchase.getCapabilities', {})
            } catch (error) {
                reject(error)
                clearCapabilitiesRequest()
            }
            return promise
        }

        function startActiveTracking() {
            if (isActiveTimeTracked.value || pingIntervalId !== undefined) {
                return
            }
            pingIntervalId = setInterval(() => {
                elapsedActiveTimeMs.value += PING_INTERVAL_MS
                if (isActiveTimeTracked.value) {
                    stopActiveTracking()
                    sendAdjustActiveTimeEvent()
                }
            }, PING_INTERVAL_MS)
        }

        function stopActiveTracking() {
            if (pingIntervalId !== undefined) {
                clearInterval(pingIntervalId)
                pingIntervalId = undefined
            }
        }

        function sendAdjustActiveTimeEvent() {
            postMessage('adjust.event', { eventToken: ADJUST_ACTIVE_TIME_EVENT_TOKEN })
        }

        function postMessage(type: string, payload: Record<string, unknown>) {
            const message = { type, payload }
            for (const handler of outgoingMessageHandlers) {
                handler(message)
            }

            return sdk.value?.postMessage(type, payload)
        }

        function addMessageHandler(handler: NativeAppMessageHandler) {
            messageHandlers.add(handler)
            sdk.value?.addMessageHandler(handler)

            return () => {
                messageHandlers.delete(handler)
                sdk.value?.removeMessageHandler(handler)
            }
        }

        function addOutgoingMessageHandler(handler: NativeAppOutgoingMessageHandler) {
            outgoingMessageHandlers.add(handler)

            return () => {
                outgoingMessageHandlers.delete(handler)
            }
        }

        function dispatchDebugMessage(data: unknown) {
            handlePurchaseCapabilities(data)
            for (const handler of messageHandlers) {
                handler(data)
            }
        }

        function dispose() {
            capabilitiesRequest?.reject(new Error('Native app was disposed'))
            clearCapabilitiesRequest()
            purchaseCapabilities.value = undefined
            stopActiveTracking()
            messageHandlers.clear()
            outgoingMessageHandlers.clear()
            sdk.value?.dispose()
        }

        return {
            sdk,
            isAvailable,
            purchaseCapabilities,
            getPurchaseCapabilities,
            elapsedActiveTimeMs,
            launchCount,
            init,
            postMessage,
            addMessageHandler,
            addOutgoingMessageHandler,
            dispatchDebugMessage,
            dispose,
        }
    },
    {
        persist: {
            key: 'native_app',
            pick: ['elapsedActiveTimeMs', 'launchCount'],
        },
    },
)
